import isUrl from "is-url";
import type { JSX } from "preact";
import { useState } from "preact/hooks";
import type { Editor } from "slate";
import { Range, Transforms } from "slate";
import { useSlate } from "slate-react";
import { ConfirmModal } from "@gss/components/ConfirmModal";
import { InputText } from "@gss/components/InputText";
import { Link } from "../components/icons";
import { Popover } from "../components/Popover";
import type { LinkElement } from "./types";
import { getMatch, getNode, isActive, removeNodes } from "./util";

export function AddLinkButton(): JSX.Element {
    const editor = useSlate();

    const onSave = (url: string) => {
        const link = getLinkElement(editor);
        if (link) {
            // Update existing link
            if (url) {
                updateLink(editor, link, url);
            }

            // Remove existing link
            else {
                unwrapLink(editor);
            }
        }

        // Insert new link
        else if (url) {
            insertLink(editor, url);
        }
    };

    return (
        <Popover
            overlay={(hide) => (
                <Overlay
                    resolve={(url) => {
                        onSave(url);
                        hide();
                    }}
                    reject={hide}
                    link={getLinkElement(editor)}
                />
            )}
        >
            <button
                type="button"
                className={isLinkActive(editor) ? "active" : undefined}
            >
                <Link />
            </button>
        </Popover>
    );
}

interface OverlayParameters {
    resolve: (url: string) => void;
    reject: () => void;
    link?: LinkElement;
}

function Overlay({ resolve, reject, link }: OverlayParameters) {
    const [value, setValue] = useState(link?.url ?? "");
    const url = value.trim();
    const isEmpty = url === "";
    const isValid = isEmpty || isUrl(url);

    return (
        <ConfirmModal
            disabled={!isValid || isEmpty}
            resolve={() => resolve(url)}
            reject={reject}
        >
            <InputText
                small
                invalid={!isValid}
                value={value}
                placeholder="Skriv adress"
                onChange={setValue}
            />
        </ConfirmModal>
    );
}

function isLinkActive(editor: Editor): boolean {
    return isActive(editor, "link");
}

function getLinkElement(editor: Editor): LinkElement | undefined {
    const node = getNode(editor, "link");
    return node?.[0];
}

function updateLink(editor: Editor, oldLink: LinkElement, url: string) {
    const newLink: LinkElement = {
        ...oldLink,
        url,
    };
    removeNodes(editor, "link");
    Transforms.insertNodes(editor, newLink);
}

function insertLink(editor: Editor, url: string) {
    const { selection } = editor;
    const isCollapsed = selection && Range.isCollapsed(selection);
    const link: LinkElement = {
        type: "link",
        url,
        children: isCollapsed ? [{ text: url }] : [],
    };

    if (isCollapsed) {
        Transforms.insertNodes(editor, link);
    } else {
        Transforms.wrapNodes(editor, link, { split: true });
        Transforms.collapse(editor, { edge: "end" });
    }
}

function unwrapLink(editor: Editor) {
    Transforms.unwrapNodes(editor, {
        match: getMatch("link"),
    });
}

export function withLink(editor: Editor): Editor {
    const { isInline } = editor;

    editor.isInline = (element) => element.type === "link" || isInline(element);

    return editor;
}
